import {
  GraduationCapIcon,
  Building2Icon,
  StoreIcon,
  BriefcaseIcon,
  UsersIcon,
  HomeIcon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { OrganizationKind, WorkspaceKind } from '../types/workspace'

/**
 * The six PUBLIC Campuzon experiences.
 *
 * These are not account types. Choosing one adds a membership to the identity
 * you already have — the same password, the same session, the same person.
 *
 * Internal administration is deliberately absent. It is not a public role and
 * cannot be reached by anything a visitor can submit, so it has no record here
 * for a client to name.
 */
export interface ExperienceOption {
  id: WorkspaceKind
  label: string
  description: string
  icon: LucideIcon
  /** null where the workspace belongs to the person rather than to an organization. */
  organizationKind: OrganizationKind | null
  /** The seeded role this membership is created with. */
  orgRoleId: string
  /** What the person is asked for before the workspace opens. */
  profileLabel: string
  profileHint: string
  /** Stated plainly, because approving a membership is not the same as verifying it. */
  verification: string
  /** Whether the membership starts granting access, or waits on a person. */
  startsActive: boolean
}

export const experiences: ExperienceOption[] = [
  {
    id: 'student',
    label: 'Student',
    description: 'Manage academics, campus life, housing, marketplace and opportunities.',
    icon: GraduationCapIcon,
    organizationKind: null,
    orgRoleId: 'orole-student',
    profileLabel: 'Your studies',
    profileHint: 'Enough to open your workspace. Everything else can wait.',
    verification:
      'Your student workspace opens straight away. Being a verified student is a separate check your institution performs — until then Campuzon shows what you told us as exactly that.',
    startsActive: true,
  },
  {
    id: 'campus',
    label: 'University / Campus',
    description: 'Connect your institution, students, services and campus community.',
    icon: Building2Icon,
    organizationKind: 'institution',
    orgRoleId: 'orole-campus-manager',
    profileLabel: 'Your institution',
    profileHint: 'We create the organization; you are its first member, not the organization itself.',
    verification:
      'An institution workspace waits on confirmation that you are authorised to act for it. Campuzon reviews this — nobody can grant it to themselves.',
    startsActive: false,
  },
  {
    id: 'provider',
    label: 'Provider',
    description: 'Sell products and services around campus.',
    icon: StoreIcon,
    organizationKind: 'provider',
    orgRoleId: 'orole-provider-owner',
    profileLabel: 'Your business',
    profileHint: 'The business is an organization. If you leave it later, your account remains.',
    verification:
      'You can set up your storefront immediately. The verified provider mark is a separate check, and it is never a guarantee of quality or refunds.',
    startsActive: true,
  },
  {
    id: 'opportunity',
    label: 'Employer / Opportunity',
    description: 'Connect students with jobs, internships and career opportunities.',
    icon: BriefcaseIcon,
    organizationKind: 'employer',
    orgRoleId: 'orole-opportunity-manager',
    profileLabel: 'Your organization',
    profileHint: 'Colleagues join the same organization later — never one employer account each.',
    verification:
      'Listings reach students once your organization is confirmed. Until then you can prepare them, and students are not shown unverified employers.',
    startsActive: false,
  },
  {
    id: 'community',
    label: 'Community',
    description: 'Build and manage your campus community.',
    icon: UsersIcon,
    organizationKind: 'community',
    orgRoleId: 'orole-community-manager',
    profileLabel: 'Your community',
    profileHint: 'Communities have members and moderators, all on their own Campuzon accounts.',
    verification:
      'Student-run communities operate without institutional recognition, and are labelled that way to everyone who sees them.',
    startsActive: true,
  },
  {
    id: 'housing',
    label: 'Housing',
    description: 'Manage student accommodation and housing.',
    icon: HomeIcon,
    organizationKind: 'provider',
    orgRoleId: 'orole-housing-manager',
    profileLabel: 'Your properties',
    profileHint:
      'Housing is a capability of a provider organization — not a separate login, and not a separate password.',
    verification:
      'Listing a property is not the same as Campuzon inspecting it. Uninspected listings say so on every screen a student sees.',
    startsActive: true,
  },
]

export function experienceById(id: string): ExperienceOption | null {
  return experiences.find((item) => item.id === id) ?? null
}
